import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Contract } from '@core/models/contract.model';
import { ContractFilter, ContractService } from '@core/services/contract/contract.service';
import { ContractCreatingDialogComponent } from '@shared/contract/contract-list/contract-creating-dialog/contract-creating-dialog.component';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-contract-list',
  templateUrl: './contract-list.component.html',
  styleUrls: ['./contract-list.component.scss'],
  standalone: false,
})
export class ContractListComponent implements OnInit, OnDestroy {
  public isLoadingItems = true;
  public resultsLength = 0;
  public showClearButton = false;
  public readonly contractYear = 2027;
  public isYearPickerDisabled = true;
  public searchFormControl = new FormControl();
  public searchTerms: string[] = [];
  public yearFormControl = new FormControl();
  public itemList: Contract[];
  public items = new MatTableDataSource<Contract>([]);

  public allDisplayedColumns: any = {
    id: { isActive: true, canBeHidden: false, label: 'Numéro client' },
    name: { isActive: true, canBeHidden: false, label: 'Nom client' },
    centralAgency: { isActive: true, canBeHidden: false, label: 'Agence' },
    contractManager: { isActive: true, canBeHidden: false, label: 'Commercial' },
    // cgvLabel: { isActive: true, canBeHidden: false, label: 'Libelle CGV' },
    stateContractLabel: { isActive: true, canBeHidden: false, label: 'Etat du contrat' },
    CA: { isActive: true, canBeHidden: false, label: 'CA' },
    cgvReturnDate: { isActive: true, canBeHidden: false, label: 'Date retour' },
    // cgvStateNum: { isActive: false, canBeHidden: true, label: 'Etat' },
    cgvStatus: { isActive: true, canBeHidden: false, label: 'Statut' },
    f2f3f5CA: { isActive: false, canBeHidden: true, label: 'CA F2/F3/F5' },
    regionCA: { isActive: false, canBeHidden: true, label: 'CA régional' },
    thematicCA: { isActive: false, canBeHidden: true, label: 'CA thématique' },
    sponsorshipFtpCA: { isActive: false, canBeHidden: true, label: 'CA parrainage' },
    sponsorshipThematicCA: { isActive: false, canBeHidden: true, label: 'CA parrainage thématique' },
    sponsorshipRegCA: { isActive: false, canBeHidden: true, label: 'CA parrainage régional' },
    sponsorshipSoReachCA: { isActive: false, canBeHidden: true, label: 'CA parrainage SoReach' },
    sponsorshipWebCA: { isActive: false, canBeHidden: true, label: 'CA parrainage web' },
    webCA: { isActive: false, canBeHidden: true, label: 'CA web' },
    OIWebCA: { isActive: false, canBeHidden: true, label: 'CA OI web' },
    open: { isActive: true, canBeHidden: false, label: 'Ouvrir' },
  };

  public displayedColumns: string[] = [];

  public filter: ContractFilter = {
    year: this.getCurrentYear(),
    // searchElementById: this.searchFormControl.value || '',
    // searchElementByName: this.searchFormControl.value || '',
  };

  componentDestroyed$: Subject<void> = new Subject();

  constructor(private contractService: ContractService, private router: Router, public dialog: MatDialog) {
    this.items.filterPredicate = (contract, filter) => {
      const searchableValues = [
        contract.clientId,
        contract.clientName,
        contract.advertiserId,
        contract.centralAgency?.id,
        contract.centralAgency?.name,
        contract.contractManager?.id,
        contract.contractManager?.name,
      ];
      const searchableText = searchableValues.filter(value => value != null).join(' ').toLocaleLowerCase();
      return filter.split('\u0000').every(term => searchableText.includes(term));
    };
  }

  ngOnInit(): void {
    this.applyFilterToTable();
    this.setCurrentYearFilter();
    this.getContractList(this.filter);
    this.handleYearFilterValueChange();
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }

  getCurrentYear() {
    return this.contractYear.toString();
  }

  setCurrentYearFilter() {
    this.yearFormControl.setValue(this.getCurrentYear());
  }

  getContractList(filter: ContractFilter) {
    this.isLoadingItems = true;

    this.contractService
      .getContractList(filter)
      .pipe(debounceTime(500), takeUntil(this.componentDestroyed$))
      .subscribe(
        contracts => {
          this.itemList = contracts;
          this.items.data = contracts;
          this.isLoadingItems = false;
        },
        error => {
          this.isLoadingItems = false;
        }
      );
  }

  handleYearDatepickerValueChange(year: number | string) {
    this.yearFormControl.setValue(year.toString());
  }

  handleYearFilterValueChange() {
    this.yearFormControl.valueChanges.pipe(takeUntil(this.componentDestroyed$)).subscribe(value => {
      this.filter.year = value.toString();
      this.getContractList(this.filter);
    });
  }

  addSearchTerm(): void {
    const term = (this.searchFormControl.value || '').trim();
    if (!term) {
      return;
    }

    if (!this.searchTerms.some(existingTerm => existingTerm.toLocaleLowerCase() === term.toLocaleLowerCase())) {
      this.searchTerms = [...this.searchTerms, term];
      this.applySearchTerms();
    }
    this.clearSearchInput();
  }

  clearSearchInput(): void {
    this.searchFormControl.setValue('');
  }

  removeSearchTerm(term: string): void {
    this.searchTerms = this.searchTerms.filter(searchTerm => searchTerm !== term);
    this.applySearchTerms();
  }

  private applySearchTerms(): void {
    this.items.filter = this.searchTerms.map(term => term.toLocaleLowerCase()).join('\u0000');
  }

  compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  sortData(sort: Sort) {
    const sortedData = this.items.data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'id':
          return this.compare(a.id, b.id, isAsc);
        case 'name':
          return this.compare(a.clientName, b.clientName, isAsc);
        case 'centralAgencyName':
          return this.compare(a.centralAgency?.name || '', b.centralAgency?.name || '', isAsc);
        default:
          return 0;
      }
    });
    this.items.data = [...sortedData];
  }

  onIconClick(contract) {
    this.router.navigate(['/commercial/contract/' + this.filter.year + '/' + contract.clientId]);
  }

  applyFilterToTable = () => {
    this.isLoadingItems = true;

    this.displayedColumns = [];
    for (const key in this.allDisplayedColumns) {
      if (this.allDisplayedColumns[key].isActive) {
        this.displayedColumns.push(key);
      }
    }

    this.isLoadingItems = false;
  };

  public onContractCreateButtonClick() {
    this.openContractCreatingDialog();
  }

  private openContractCreatingDialog(): void {
    // TODO : delete contract with @bvu view
    const dialogRef = this.dialog.open(ContractCreatingDialogComponent, {
      width: '600px',
      height: '385px',
      disableClose: false,
      data: { year: this.contractYear.toString() },
      panelClass: 'tv-dialog-container',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(result => {
        this.getContractList(this.filter);
      });
  }
}
